import { VisuallyHidden } from "./VisuallyHidden";
import { trackedDealUrl } from "../lib/click";
import { formatMoney, formatPercentOff } from "../lib/format";
import styles from "./MentionCard.module.css";
import type { BoardEntry } from "../api/types";

/**
 * An Honorable Mention: the design canvas's card.
 *
 * It was a card until `WEB-010` made it a one-line row, and `WEB-026` made it a card again
 * when the operator asked for the drawing exactly. The shape is the part worth having back.
 * Canon § 10 requires a mention never to read as a Top 10 position, and a card carries that
 * separation *by shape*: it is not row-shaped, so it cannot be mistaken for a board row.
 * `WEB-010`'s row had to carry the same separation by absence — no rank mark, no brass, no
 * price triplet, no button — which is a weaker guarantee, because absence is what a later
 * change quietly erodes. The card keeps the absences too; it just no longer depends on them.
 *
 * Four parts, in the canvas's order: name, description, deal value, action. The value is the
 * canvas's single compact figure rather than the board row's price triplet, and it is smaller
 * than any board price on purpose — a mention is not competing for a position, and a figure
 * that shouted louder than rank 10's would undo the separation this section exists to draw.
 *
 * The action is the one thing the canvas does not draw. Canon § 13 says a buyer can open a
 * deal on the seller's destination URL, and canon § 11 counts that click towards the weekly
 * and monthly lists through the `honorable` surface. It stays the underlined anchor `WEB-010`
 * established rather than becoming the board's button: a button is the single piece of board
 * furniture that would hand this card the shape it is here to avoid.
 */
export function MentionCard({ entry }: { entry: BoardEntry }) {
  const { deal } = entry;
  const href = trackedDealUrl(deal.id, "honorable", deal.dealUrl);

  const hasPrices = deal.originalPriceCents !== null && deal.dealPriceCents !== null;
  // One compact value, not the board row's triplet. A percentage where there is one;
  // otherwise the deal's own words (canon § 2 lets a deal express its value in words).
  const value = hasPrices
    ? `${formatMoney(deal.dealPriceCents ?? 0)}${
        deal.discountPercent === null ? "" : ` · ${formatPercentOff(deal.discountPercent)}`
      }`
    : deal.valueNotes;

  return (
    <article className={styles.card}>
      <h3 className={styles.product}>{deal.productName}</h3>
      <p className={styles.description}>{deal.shortDescription}</p>
      {value === null ? null : <p className={`${styles.value} figure`}>{value}</p>}
      {href === null ? (
        // docs/SECURITY.md § URLs: an unsafe destination gets no link, tracked or direct.
        // The card keeps its information and loses only the action.
        <span className={styles.unavailable}>Link unavailable</span>
      ) : (
        <a className={styles.action} href={href} target="_blank" rel="noopener noreferrer">
          Get this deal
          <VisuallyHidden> — {deal.productName} (opens in a new tab)</VisuallyHidden>
        </a>
      )}
    </article>
  );
}
